import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Pagination,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  MenuItem,
  Divider,
  styled,
  alpha,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Notifications as NotificationsIcon,
  Campaign as CampaignIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { blue, green, amber, red, grey } from "@mui/material/colors";
import useAdminContext from "../../store/contexts/AdminContext";
import DeleteConfirmDialog from "./DeleteConfirmDialog";

// Styled Components
const StatsCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#ffffff",
  border: "1px solid #e3f2fd",
  borderRadius: "16px",
  transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 24px rgba(33, 150, 243, 0.15)",
    border: "1px solid #2196f3",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: alpha(blue[500], 0.1),
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const AdminNotificationManagement = () => {
  const {
    getNotificationsApiCall,
    getNotificationByIdApiCall,
    createNotificationApiCall,
    updateNotificationApiCall,
    deleteNotificationApiCall,
  } = useAdminContext();

  // Notification State
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const [dialogMode, setDialogMode] = useState("create");
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    type: "info",
    priority: "medium",
    is_active: true,
  });

  // Stats state
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    totalRecipients: 0,
    avgReadRate: 0,
  });

  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchNotifications = async () => {
    setLoading(true);
    const result = await getNotificationsApiCall({
      pageNo: currentPage,
      pageSize: 20,
    });

    if (result.success) {
      setNotifications(result.data.notifications);
      setTotalPages(result.data.pagination.totalPages);
      setTotalItems(result.data.pagination.totalItems);

      // Calculate stats
      const total = result.data.notifications.length;
      const active = result.data.notifications.filter(
        (n) => n.is_active,
      ).length;
      const totalRecipients = result.data.notifications.reduce(
        (sum, n) => sum + n.statistics.total_recipients,
        0,
      );
      const avgReadRate =
        total > 0
          ? result.data.notifications.reduce((sum, n) => {
              const rate =
                n.statistics.total_recipients > 0
                  ? (n.statistics.read_count / n.statistics.total_recipients) *
                    100
                  : 0;
              return sum + rate;
            }, 0) / total
          : 0;

      setStats({
        total: totalItems || total,
        active,
        totalRecipients,
        avgReadRate: avgReadRate.toFixed(1),
      });
    }
    setLoading(false);
  };

  const handleOpenDialog = async (mode, notification = null) => {
    setDialogMode(mode);

    if (mode === "create") {
      setFormData({
        title: "",
        message: "",
        type: "info",
        priority: "medium",
        is_active: true,
      });
      setSelectedNotification(null);
    } else if ((mode === "edit" || mode === "view") && notification) {
      const result = await getNotificationByIdApiCall(notification.id);
      if (result.success) {
        setSelectedNotification(result.data);
        setFormData({
          title: result.data.title,
          message: result.data.message,
          type: result.data.type,
          priority: result.data.priority,
          is_active: result.data.is_active === 1,
        });
      }
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedNotification(null);
  };

  const handleInputChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    const submitData = {
      ...formData,
      is_active: formData.is_active,
    };

    let result;
    if (dialogMode === "create") {
      result = await createNotificationApiCall(submitData);
    } else if (dialogMode === "edit") {
      result = await updateNotificationApiCall(
        selectedNotification.id,
        submitData,
      );
    }

    if (result && result.success) {
      handleCloseDialog();
      fetchNotifications();
    }
  };

  const handleOpenDeleteDialog = (notification) => {
    setNotificationToDelete(notification);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setNotificationToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!notificationToDelete) return;

    const result = await deleteNotificationApiCall(notificationToDelete.id);
    if (result && result.success) {
      handleCloseDeleteDialog();
      fetchNotifications();
    }
  };

  const getTypeChip = (type) => {
    const colors = {
      info: "info",
      warning: "warning",
      success: "success",
      error: "error",
    };
    return (
      <Chip label={type.toUpperCase()} size="small" color={colors[type]} />
    );
  };

  const getPriorityChip = (priority) => {
    const colors = {
      high: "error",
      medium: "warning",
      low: "default",
    };
    return (
      <Chip
        label={priority.toUpperCase()}
        size="small"
        color={colors[priority]}
      />
    );
  };

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: "#1a1a1a" }}>
              Notification Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Create and manage system-wide notifications for all users
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog("create")}
            sx={{
              backgroundColor: blue[500],
              "&:hover": {
                backgroundColor: blue[600],
              },
              borderRadius: "8px",
              textTransform: "none",
              px: 3,
            }}
          >
            Create Notification
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Total Notifications
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 600, color: blue[700] }}
                    >
                      {stats.total}
                    </Typography>
                  </Box>
                  <NotificationsIcon sx={{ fontSize: 40, color: blue[500] }} />
                </Box>
              </CardContent>
            </StatsCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatsCard>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Active
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 600, color: green[700] }}
                    >
                      {stats.active}
                    </Typography>
                  </Box>
                  <CheckCircleIcon sx={{ fontSize: 40, color: green[500] }} />
                </Box>
              </CardContent>
            </StatsCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatsCard>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Total Recipients
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 600, color: amber[700] }}
                    >
                      {stats.totalRecipients}
                    </Typography>
                  </Box>
                  <PeopleIcon sx={{ fontSize: 40, color: amber[500] }} />
                </Box>
              </CardContent>
            </StatsCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatsCard>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Avg Read Rate
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 600, color: blue[700] }}
                    >
                      {stats.avgReadRate}%
                    </Typography>
                  </Box>
                  <CampaignIcon sx={{ fontSize: 40, color: blue[500] }} />
                </Box>
              </CardContent>
            </StatsCard>
          </Grid>
        </Grid>
      </Box>

      {/* Table Section */}
      <Paper
        elevation={3}
        sx={{
          borderRadius: "12px",
          border: "1px solid #e0e0e0",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : notifications.length === 0 ? (
          <Box p={4}>
            <Alert severity="info">
              No notifications found. Create your first notification to get
              started.
            </Alert>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: grey[100] }}>
                    <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Recipients</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Read Rate</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notifications.map((notification) => {
                    const readRate =
                      notification.statistics.total_recipients > 0
                        ? (
                            (notification.statistics.read_count /
                              notification.statistics.total_recipients) *
                            100
                          ).toFixed(1)
                        : 0;

                    return (
                      <StyledTableRow key={notification.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {notification.title}
                          </Typography>
                        </TableCell>
                        <TableCell>{getTypeChip(notification.type)}</TableCell>
                        <TableCell>
                          {getPriorityChip(notification.priority)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              notification.is_active ? "ACTIVE" : "INACTIVE"
                            }
                            size="small"
                            color={
                              notification.is_active ? "success" : "default"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {notification.statistics.total_recipients}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {notification.statistics.read_count} /{" "}
                              {notification.statistics.total_recipients}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ({readRate}%)
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(
                              notification.created_at,
                            ).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleOpenDialog("view", notification)
                              }
                              title="View Details"
                              sx={{ color: blue[600] }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleOpenDialog("edit", notification)
                              }
                              title="Edit"
                              sx={{ color: amber[700] }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleOpenDeleteDialog(notification)
                              }
                              title="Delete"
                              sx={{ color: red[600] }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={2}
                sx={{ borderTop: "1px solid #e0e0e0" }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {notifications.length} of {totalItems} notifications
                </Typography>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(e, page) => setCurrentPage(page)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Create/Edit/View Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #e0e0e0", mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {dialogMode === "create"
              ? "Create Notification"
              : dialogMode === "edit"
                ? "Edit Notification"
                : "View Notification"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={handleInputChange("title")}
              disabled={dialogMode === "view"}
              required
            />

            <TextField
              fullWidth
              label="Message"
              value={formData.message}
              onChange={handleInputChange("message")}
              disabled={dialogMode === "view"}
              multiline
              rows={4}
              required
            />

            <FormControl fullWidth>
              <InputLabel shrink>Type</InputLabel>
              <Select
                value={formData.type}
                onChange={handleInputChange("type")}
                disabled={dialogMode === "view"}
                label="Type"
                notched
              >
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel shrink>Priority</InputLabel>
              <Select
                value={formData.priority}
                onChange={handleInputChange("priority")}
                disabled={dialogMode === "view"}
                label="Priority"
                notched
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={handleInputChange("is_active")}
                  disabled={dialogMode === "view"}
                />
              }
              label="Active (will broadcast to all users when active)"
            />

            {dialogMode === "view" && selectedNotification && (
              <Paper
                elevation={0}
                sx={{ mt: 1, p: 2, bgcolor: grey[50], borderRadius: 2 }}
              >
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  Statistics
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Recipients:{" "}
                    <strong>
                      {selectedNotification.statistics.total_recipients}
                    </strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Read:{" "}
                    <strong>
                      {selectedNotification.statistics.read_count}
                    </strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Unread:{" "}
                    <strong>
                      {selectedNotification.statistics.unread_count}
                    </strong>
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Created:{" "}
                    {new Date(selectedNotification.created_at).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last Updated:{" "}
                    {new Date(selectedNotification.updated_at).toLocaleString()}
                  </Typography>
                </Box>
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <Button onClick={handleCloseDialog} sx={{ textTransform: "none" }}>
            {dialogMode === "view" ? "Close" : "Cancel"}
          </Button>
          {dialogMode !== "view" && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              sx={{
                backgroundColor: blue[500],
                "&:hover": {
                  backgroundColor: blue[600],
                },
                textTransform: "none",
              }}
            >
              {dialogMode === "create" ? "Create" : "Update"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Delete Notification"
        itemType="notification"
        itemName={notificationToDelete?.title}
        warningMessage="Are you sure you want to delete this notification?"
        additionalInfo="All recipients will no longer see this notification, and all associated statistics will be permanently removed."
        confirmButtonText="Delete Notification"
      />
    </Box>
  );
};

export default AdminNotificationManagement;
